/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 *
 ********************************************************************!*/

import { GetterTree } from "vuex";
import { IInboxState } from "./state";
import { IRootState } from "@/store/modules/root/state";
import { IChat, IChatMessage } from "@chatcaptain/types/dist/chatbot";
import { ITag, ITagColor } from "@/types/inbox/tags";
import { InboxStore } from "@/store/constants";
import { store } from "@/store";
import { IAnswer } from "@/types/inbox/answers";
import { ICustomView } from "@/types/inbox/views";
import { ICustomField } from "@/types/inbox/fields";
import {
  IGroupResult,
  IUser,
  IUserResult,
} from "@chatcaptain/types/dist/organization";

export enum Getters {
  GET_CHATBOT_ID = "GET_CHATBOT_ID",
  GET_CONVERSATIONS = "GET_CONVERSATIONS",
  GET_CONVERSATIONS_LOADING = "GET_CONVERSATIONS_LOADING",
  GET_CONVERSATION = "GET_CONVERSATION",
  GET_CURRENT_CONVERSATION = "GET_CURRENT_CONVERSATION",
  GET_CURRENT_CONVERSATION_ID = "GET_CURRENT_CONVERSATION_ID",
  GET_CURRENT_CONVERSATION_LOADING = "GET_CURRENT_CONVERSATION_LOADING",
  GET_CURRENT_MESSAGES = "GET_CURRENT_MESSAGES",
  GET_TAG_COLORS = "GET_TAG_COLORS",
  GET_TAG_COLOR = "GET_TAG_COLOR",
  GET_TAGS = "GET_TAGS",
  GET_TAG = "GET_TAG",
  GET_ANSWERS = "GET_ANSWERS",
  GET_VIEWS = "GET_VIEWS",
  GET_VIEW = "GET_VIEW",
  GET_FIELDS = "GET_FIELDS",
  GET_LOCATION = "GET_LOCATION",
  //   GET_USERS = "GET_USERS",
  //   GET_USER = "GET_USER",
  //   GET_GROUPS = "GET_GROUPS",
}

export interface IGetters {
  [Getters.GET_CHATBOT_ID](state: IInboxState): string;
  [Getters.GET_CONVERSATIONS](state: IInboxState): IChat[];
  [Getters.GET_CONVERSATIONS_LOADING](state: IInboxState): boolean;
  [Getters.GET_CONVERSATION](
    state: IInboxState
  ): (id: string) => IChat | undefined;
  [Getters.GET_CURRENT_CONVERSATION](state: IInboxState): IChat | null;
  [Getters.GET_CURRENT_CONVERSATION_ID](state: IInboxState): string | null;
  [Getters.GET_CURRENT_CONVERSATION_LOADING](state: IInboxState): boolean;
  [Getters.GET_CURRENT_MESSAGES](state: IInboxState): IChatMessage[] | null;
  [Getters.GET_TAG_COLORS](state: IInboxState): { [key: string]: ITagColor };
  [Getters.GET_TAG_COLOR](state: IInboxState): (name: string) => ITagColor;
  [Getters.GET_TAGS](state: IInboxState): ITag[] | null;
  [Getters.GET_TAG](state: IInboxState): (id: string) => ITag | null;
  [Getters.GET_ANSWERS](state: IInboxState): IAnswer[] | null;
  [Getters.GET_VIEWS](state: IInboxState): ICustomView[] | null;
  [Getters.GET_VIEW](state: IInboxState): (id: string) => ICustomView | null;
  [Getters.GET_FIELDS](state: IInboxState): ICustomField[] | null;
  [Getters.GET_LOCATION](state: IInboxState): IChat["location"] | null;
  // [Getters.GET_USERS](state: IInboxState): IUserResult[] | null;
  // [Getters.GET_USER](state: IInboxState): (id: string) => IUserResult | null;
  // [Getters.GET_GROUPS](state: IInboxState): IGroupResult[] | null;
}

export const getters: GetterTree<IInboxState, IRootState> & IGetters = {
  [Getters.GET_CHATBOT_ID](state: IInboxState): string {
    return state.chatbotId;
  },
  [Getters.GET_CONVERSATIONS]: (state: IInboxState) => {
    return state.conversations;
  },
  [Getters.GET_CONVERSATIONS_LOADING]: (state: IInboxState) => {
    return state.conversationsLoading;
  },
  [Getters.GET_CONVERSATION]: (state: IInboxState) => (id: string) => {
    return state.conversations.find((c) => c.id === id);
  },
  [Getters.GET_CURRENT_CONVERSATION]: (state: IInboxState) => {
    if (
      state.currentConversationId &&
      (!state.currentConversation ||
        state.currentConversation.id !== state.currentConversationId) &&
      !state.currentConversationLoading
    ) {
      store.commit(InboxStore.Mutations.SET_CURRENT_CONVERSATION_LOADING, true);
      store.dispatch(InboxStore.Actions.LOAD_CURRENT_CONVERSATION);
      return null;
    }
    return state.currentConversation;
  },
  [Getters.GET_CURRENT_CONVERSATION_ID]: (state: IInboxState) => {
    return state.currentConversationId;
  },
  [Getters.GET_CURRENT_CONVERSATION_LOADING]: (state: IInboxState) => {
    return state.currentConversationLoading;
  },
  [Getters.GET_CURRENT_MESSAGES]: (state: IInboxState) => {
    return state.currentMessages;
  },
  [Getters.GET_TAG_COLORS]: (state: IInboxState) => {
    return state.tagColors;
  },
  [Getters.GET_TAG_COLOR]: (state: IInboxState) => (name: string) => {
    return state.tagColors[name];
  },
  [Getters.GET_TAGS]: (state: IInboxState) => {
    if (!state.tags) {
      store.dispatch(InboxStore.Actions.LOAD_TAGS);
      return null;
    }
    return state.tags;
  },
  [Getters.GET_TAG]: (state: IInboxState) => (id: string) => {
    if (!state.tags) {
      store.dispatch(InboxStore.Actions.LOAD_TAGS);
      return null;
    }
    return state.tags.find((tag) => tag.id === id) || null;
  },
  [Getters.GET_ANSWERS]: (state: IInboxState) => {
    if (!state.answers) {
      store.dispatch(InboxStore.Actions.LOAD_ANSWERS);
      return null;
    }
    return state.answers;
  },
  [Getters.GET_VIEWS]: (state: IInboxState) => {
    if (!state.views) {
      store.dispatch(InboxStore.Actions.LOAD_VIEWS);
      return null;
    }
    return state.views;
  },
  [Getters.GET_VIEW]: (state: IInboxState) => (id: string) => {
    if (!state.views) {
      store.dispatch(InboxStore.Actions.LOAD_VIEWS);
      return null;
    }
    return state.views.find((view) => view.id === id) || null;
  },
  [Getters.GET_FIELDS]: (state: IInboxState) => {
    if (!state.fields) {
      store.dispatch(InboxStore.Actions.LOAD_FIELDS);
      return null;
    }
    return state.fields;
  },
  [Getters.GET_LOCATION]: (state: IInboxState) => {
    if (
      state.currentConversationId &&
      !state.location[state.currentConversationId]
    ) {
      store.dispatch(InboxStore.Actions.LOAD_LOCATION);
      return null;
    }
    if (state.currentConversationId) {
      return state.location[state.currentConversationId];
    }
  },

  // [Getters.GET_USERS]: (state: IInboxState) => {
  //   if (!state.users) {
  //     store.dispatch(InboxStore.Actions.LOAD_USERS);
  //     return null;
  //   }
  //   return state.users;
  // },
  // [Getters.GET_USER]: (state: IInboxState) => (id: string) => {
  //   if (!state.users) {
  //     store.dispatch(InboxStore.Actions.LOAD_USERS);
  //     return null;
  //   }
  //   return state.users.find((user) => user.id === id) || null;
  // },
  // [Getters.GET_GROUPS]: (state: IInboxState) => {
  //   if (!state.groups) {
  //     store.dispatch(InboxStore.Actions.LOAD_GROUPS);
  //     return null;
  //   }
  //   return state.groups || [];
  // },
};
